'use client';

import {
  createSubscriberQueryFn,
  getSubscriberQueryMetaOptions,
  isUserPortfolioSnapshotWire,
  type UseQueryResult,
  type UserPortfolioSnapshotWire,
  type UserPortfolioUnvalidatedValue,
  useIsFetchedThisSession,
  useQuery,
  useQueryClient,
} from '@morfi/backend-sdk-v2/client';
import { useMemo } from 'react'; // ARBITRARY: TanStack Query requires stable observer identities.
import { userPortfolioSnapshotQueryKey } from './query-key';

export function useUserPortfolioSnapshotQuerySelect<TSelected>(
  userId: string,
  select: (snapshot: UserPortfolioSnapshotWire | null) => TSelected,
): UseQueryResult<TSelected, Error> {
  const guardedSelect = useMemo(() => guardedSnapshotSelect(select), [select]); // ARBITRARY: TanStack Query requires a stable validated select identity.
  return useUserPortfolioSnapshotQueryObserver(userId, guardedSelect);
}

/** False while the snapshot is only a persisted copy from a previous session. */
export function useUserPortfolioSnapshotFetchedThisSession(userId: string): boolean {
  return useIsFetchedThisSession(userPortfolioSnapshotQueryKey(userId));
}

function useUserPortfolioSnapshotQueryObserver<TSelected>(
  userId: string,
  select: (snapshot: UserPortfolioUnvalidatedValue) => TSelected,
): UseQueryResult<TSelected, Error> {
  const queryClient = useQueryClient();
  const queryKey = useMemo(() => userPortfolioSnapshotQueryKey(userId), [userId]); // ARBITRARY: TanStack Query requires stable same-key observer identity.
  const queryFn = useMemo(() => subscriberQueryFn(queryClient, queryKey), [queryClient, queryKey]); // ARBITRARY: TanStack Query latches the latest observer queryFn identity.

  return useQuery<UserPortfolioUnvalidatedValue, Error, TSelected>({
    queryKey,
    queryFn,
    ...getSubscriberQueryMetaOptions(queryClient, queryKey),
    enabled: false,
    staleTime: Number.POSITIVE_INFINITY,
    select,
  });
}

function guardedSnapshotSelect<TSelected>(
  select: (snapshot: UserPortfolioSnapshotWire | null) => TSelected,
): (snapshot: UserPortfolioUnvalidatedValue) => TSelected {
  return (snapshot: UserPortfolioUnvalidatedValue): TSelected =>
    select(isUserPortfolioSnapshotWire(snapshot) ? snapshot : null);
}

function subscriberQueryFn(
  queryClient: Parameters<typeof createSubscriberQueryFn<UserPortfolioUnvalidatedValue>>[0],
  queryKey: Parameters<typeof createSubscriberQueryFn<UserPortfolioUnvalidatedValue>>[1],
): ReturnType<typeof createSubscriberQueryFn<UserPortfolioUnvalidatedValue>> {
  return createSubscriberQueryFn<UserPortfolioUnvalidatedValue>(queryClient, queryKey);
}
